/**
 * Who is signed in (SEC-03) — the React half of the session.
 *
 * Two modes, and the distinction is deliberate:
 *
 *   LIVE  (VITE_SUPABASE_URL set) — a real GoTrue session is REQUIRED. The app
 *         shell does not render until someone has signed in, because every
 *         request needs that person's token and `auth.uid()` is what every RLS
 *         policy and audit row is written against.
 *
 *   FIXTURE (no URL) — standalone dev, the demo, and the whole browser test
 *         suite. There is no server to authenticate against, so requiring a
 *         sign-in would mean nothing but a wall in front of a fixture. The
 *         persona switcher already models "who am I" there.
 *
 * Keeping those apart is what lets a real login exist without turning 208
 * browser tests into tests of a login form.
 */
import { useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { AuthClient } from './session.js'
import { AccountRoster, type DeviceAccount } from './accounts.js'
import { InjectedAuthProvider, useInjectedAuth } from './provider.js'
export { type AuthState } from './provider.js'

const env = import.meta.env as Record<string, string | undefined>

const supabaseUrl = env.VITE_SUPABASE_URL
/** GoTrue may be routed through the gateway (/auth/v1) or exposed directly. */
const authUrl = env.VITE_AUTH_URL ?? (supabaseUrl ? `${supabaseUrl.replace(/\/$/, '')}/auth/v1` : '')

/**
 * Live mode is decided by the URL alone. It used to also require
 * VITE_SUPABASE_TOKEN — which is precisely the build-time credential this
 * removes, so a deployment that still sets one gets no benefit from it.
 */
export const isLive = Boolean(supabaseUrl)

/**
 * The accounts this device has signed in as (#1053 4b).
 *
 * Shared by every host that imports this module, because "which accounts have
 * signed in on this machine" is a property of the device and the origin, not of
 * the product on top of it. Whether a host SHOWS a switcher is that host's
 * decision — the console passes `accounts` to `UserMenu`, the portal does not,
 * exactly as it does not pass `appearance`. On a host with no switcher the
 * roster holds the one address already sitting in `solum.session` beside it,
 * and `signOut` removes both.
 */
export const accountRoster = new AccountRoster()

export const authClient = new AuthClient({
  url: authUrl, anonKey: env.VITE_SUPABASE_ANON_KEY, accounts: accountRoster,
})

/**
 * The device's accounts, as an external store. A component that renders the
 * switcher re-reads when a sign-in or a sign-out changes the list — the roster
 * is written by `AuthClient`, not by React, so there is nothing else to
 * subscribe to.
 *
 * `getSnapshot` must be referentially stable between renders or React loops, and
 * `list()` builds a new array every call. The snapshot is therefore the
 * SERIALIZED list and the parse happens in a `useMemo` keyed on it.
 */
export function useDeviceAccounts(): DeviceAccount[] {
  const snapshot = useSyncExternalStore(
    (cb) => accountRoster.subscribe(cb),
    () => JSON.stringify(accountRoster.list()),
    () => '[]',
  )
  return useMemo(() => JSON.parse(snapshot) as DeviceAccount[], [snapshot])
}

/** The address THIS TAB is on its way to sign in as, or null. */
export function pendingSwitchEmail(): string | null {
  return accountRoster.pendingSwitch()
}

/**
 * The signed-in principal's id, or null in fixture mode.
 *
 * Several places need "who am I" outside React — the CRM experience defaults,
 * user-scoped AI guidance. They each used to decode the build-time JWT, which
 * meant they all agreed on the wrong answer. One accessor, one source.
 */
export function currentPrincipalId(): string | null {
  return authClient.current?.user.id || null
}

/**
 * A live access token for a hand-rolled fetch (the copilot endpoint, the
 * interactions drafter), refreshed if stale. Null when not signed in, so callers
 * degrade to "not configured" instead of sending `Bearer undefined`.
 */
export async function accessToken(): Promise<string | null> {
  if (!isLive || !authClient.current) return null
  try { return await authClient.getAccessToken() } catch { return null }
}

export const useAuth = useInjectedAuth

export interface AuthProviderProps {
  children: ReactNode
  /** External hosts inject their configured canonical client; no second session wrapper is needed. */
  client?: AuthClient
  /** Defaults to true for an injected client and to the local build profile otherwise. */
  live?: boolean
}

export function AuthProvider({ children, client = authClient, live }: AuthProviderProps) {
  return (
    <InjectedAuthProvider client={client} live={live ?? (client === authClient ? isLive : true)}>
      {children}
    </InjectedAuthProvider>
  )
}
